import { Emergency, Responder, MatchResult, ResponderSkill } from '../types';
import { calculateDistance } from './geolocation';
import { AIEmergencyEngine } from './aiEngine';

export class EnhancedMatchingEngine {
  private aiEngine: AIEmergencyEngine;

  constructor() {
    this.aiEngine = new AIEmergencyEngine();
  }

  async findOptimalResponder(
    emergency: Emergency,
    responders: Responder[]
  ): Promise<MatchResult | null> {
    // Filter available responders
    const availableResponders = responders.filter(responder => {
      return responder.isAvailable && 
             responder.status === 'available' &&
             responder.blockchainVerification?.verificationStatus === 'verified';
    });

    if (availableResponders.length === 0) {
      return null;
    }

    // Get AI classification for better matching
    const aiClassification = await this.aiEngine.classifyEmergency(emergency.description);

    // Calculate match scores for each responder
    const matches: MatchResult[] = [];

    for (const responder of availableResponders) {
      const distance = calculateDistance(
        emergency.location.latitude,
        emergency.location.longitude,
        responder.location.latitude,
        responder.location.longitude
      );

      // Get AI-optimized routing
      const routingData = await this.aiEngine.predictOptimalRoute(
        responder.location,
        emergency.location,
        responder.skills.map(skill => skill.name)
      );

      const skillMatch = this.calculateSkillMatch(emergency, responder, aiClassification);
      const experienceScore = this.calculateExperienceScore(responder);
      const availabilityScore = this.calculateAvailabilityScore(responder);
      const aiRecommendationScore = this.calculateAIRecommendationScore(
        emergency,
        responder,
        aiClassification
      );

      // Composite scoring algorithm
      const totalScore = (
        (1 / (distance + 1)) * 0.3 +           // Distance weight: 30%
        skillMatch * 0.25 +                     // Skill match: 25%
        experienceScore * 0.2 +                 // Experience: 20%
        availabilityScore * 0.1 +               // Availability: 10%
        aiRecommendationScore * 0.15            // AI recommendation: 15%
      );

      matches.push({
        responderId: responder.id,
        distance,
        estimatedArrival: routingData.estimatedTime,
        skillMatch,
        aiRecommendationScore: totalScore
      });
    }

    // Sort by AI recommendation score (highest first)
    matches.sort((a, b) => b.aiRecommendationScore - a.aiRecommendationScore);

    return matches[0] || null;
  }

  private calculateSkillMatch(
    emergency: Emergency,
    responder: Responder,
    aiClassification: any
  ): number {
    const requiredSkills = this.getRequiredSkills(emergency.type, emergency.severity);
    const responderSkills = responder.skills.map(skill => skill.name.toLowerCase());

    let matchScore = 0;
    let totalRequired = requiredSkills.length;

    requiredSkills.forEach(required => {
      const hasSkill = responderSkills.some(skill => 
        skill.includes(required.toLowerCase()) || required.toLowerCase().includes(skill)
      );
      
      if (hasSkill) {
        matchScore += 1;
        
        // Bonus for advanced skill level
        const skillLevel = responder.skills.find(s => 
          s.name.toLowerCase().includes(required.toLowerCase())
        )?.level;
        
        if (skillLevel === 'expert') matchScore += 0.5;
        else if (skillLevel === 'advanced') matchScore += 0.3;
        else if (skillLevel === 'intermediate') matchScore += 0.1;
      }
    });

    // Category match bonus
    if (this.categoryMatches(emergency.type, responder.category)) {
      matchScore += 1;
    }

    // AI classification bonus
    if (aiClassification.confidence > 0.8) {
      matchScore += 0.5;
    }

    return Math.min(matchScore / totalRequired, 1);
  }

  private calculateExperienceScore(responder: Responder): number {
    if (!responder.performanceMetrics) return 0.5;

    const metrics = responder.performanceMetrics;
    
    // Normalize metrics to 0-1 scale
    const responseScore = Math.min(metrics.totalResponses / 100, 1);
    const timeScore = Math.max(0, 1 - (metrics.averageResponseTime / 30)); // 30 min baseline
    const successScore = metrics.successRate;
    const ratingScore = metrics.rating / 5;

    return (responseScore * 0.3 + timeScore * 0.3 + successScore * 0.2 + ratingScore * 0.2);
  }

  private calculateAvailabilityScore(responder: Responder): number {
    let score = 1;

    // Reduce score if responder was recently active
    if (responder.lastLocationUpdate) {
      const timeSinceUpdate = Date.now() - responder.lastLocationUpdate.getTime();
      const hoursSinceUpdate = timeSinceUpdate / (1000 * 60 * 60);
      
      if (hoursSinceUpdate < 1) score *= 0.9; // Recently active
      if (hoursSinceUpdate > 8) score *= 0.8; // Long time inactive
    }

    // Online status bonus
    if (responder.isOnline) score *= 1.1;

    return Math.min(score, 1);
  }

  private calculateAIRecommendationScore(
    emergency: Emergency,
    responder: Responder,
    aiClassification: any
  ): number {
    let score = 0.5; // Base score

    // Emergency severity matching
    const severityUrgency = {
      critical: 1.0,
      high: 0.8,
      medium: 0.6,
      low: 0.4
    };

    score += severityUrgency[emergency.severity] * 0.3;

    // AI confidence bonus
    score += aiClassification.confidence * 0.2;

    // Risk factor handling
    if (aiClassification.riskFactors && aiClassification.riskFactors.length > 0) {
      const canHandleRisks = this.canHandleRiskFactors(responder, aiClassification.riskFactors);
      score += canHandleRisks ? 0.3 : -0.2;
    }

    // Specialization bonus
    if (responder.performanceMetrics?.specializations) {
      const hasRelevantSpecialization = responder.performanceMetrics.specializations.some(spec =>
        aiClassification.keywords.some((keyword: string) => 
          spec.toLowerCase().includes(keyword.toLowerCase())
        )
      );
      if (hasRelevantSpecialization) score += 0.2;
    }

    return Math.min(Math.max(score, 0), 1);
  }

  private getRequiredSkills(type: string, severity: string): string[] {
    const skillMap: Record<string, string[]> = {
      fire: ['firefighting', 'hazmat', 'rescue', 'first aid'],
      medical: ['emergency medicine', 'cpr', 'trauma care', 'life support'],
      police: ['law enforcement', 'crisis intervention', 'crowd control'],
      rescue: ['technical rescue', 'water rescue', 'rope access', 'first aid'],
      accident: ['traffic control', 'first aid', 'vehicle extrication']
    };

    const baseSkills = skillMap[type] || ['first aid'];

    // Add severity-specific skills
    if (severity === 'critical') {
      baseSkills.push('advanced life support', 'trauma surgery');
    } else if (severity === 'high') {
      baseSkills.push('emergency response', 'crisis management');
    }

    return baseSkills;
  }

  private categoryMatches(emergencyType: string, responderCategory: string): boolean {
    const categoryMap: Record<string, string[]> = {
      fire: ['fire'],
      medical: ['medical'],
      police: ['police'],
      rescue: ['rescue', 'fire'],
      accident: ['medical', 'police']
    };

    const validCategories = categoryMap[emergencyType] || [];
    return validCategories.includes(responderCategory) || responderCategory === 'volunteer';
  }

  private canHandleRiskFactors(responder: Responder, riskFactors: string[]): boolean {
    const responderCapabilities = responder.skills.map(skill => skill.name.toLowerCase());
    
    const riskHandlingMap: Record<string, string[]> = {
      'hazardous materials': ['hazmat', 'chemical response'],
      'multiple casualties': ['mass casualty', 'triage'],
      'structural damage': ['technical rescue', 'structural assessment'],
      'pediatric emergency': ['pediatric care', 'child psychology'],
      'mental health crisis': ['crisis intervention', 'mental health']
    };

    return riskFactors.every(risk => {
      const requiredCapabilities = riskHandlingMap[risk] || [];
      return requiredCapabilities.some(capability =>
        responderCapabilities.some(skill => skill.includes(capability))
      );
    });
  }

  async generateEmergencyInsights(emergencies: Emergency[]): Promise<{
    patterns: any[];
    predictions: any[];
    recommendations: string[];
  }> {
    const patterns = await this.aiEngine.analyzeEmergencyPatterns(emergencies);
    
    const predictions = [
      {
        type: 'resource_allocation',
        prediction: 'Increased medical emergencies expected during evening hours',
        confidence: 0.85,
        timeframe: '6-8 PM today'
      },
      {
        type: 'weather_impact',
        prediction: 'Higher accident rates due to weather conditions',
        confidence: 0.72,
        timeframe: 'Next 4 hours'
      }
    ];

    const recommendations = [
      'Deploy additional medical units to high-traffic areas',
      'Pre-position rescue equipment near construction zones',
      'Increase patrol frequency in accident-prone locations',
      'Activate weather-related emergency protocols'
    ];

    return {
      patterns: patterns.patterns,
      predictions,
      recommendations: [...patterns.recommendations, ...recommendations]
    };
  }
}